/**
 * @fileoverview Controller for the Specify Listings Configuration page
 *
 * @author Heesun Jun (hjun@yext.com)
 */
import yext from 'goog:yext';

import {useAjax} from '/src/com/yext/listingslaunchstorm/js/common/hooks/useajax';
import {ErrorCallout} from '/src/com/yext/listingslaunchstorm/js/enterprise/errorcallout/errorcallout';
import {AltBrandNamesConfig} from '/src/com/yext/listingslaunchstorm/js/enterprise/specifylistingconfiguration/data/altbrandnamesconfig';
import {DeliveryConfiguration} from '/src/com/yext/listingslaunchstorm/js/enterprise/specifylistingconfiguration/data/deliveryconfiguration';
import {ListingConfiguration} from '/src/com/yext/listingslaunchstorm/js/enterprise/specifylistingconfiguration/data/listingsconfiguration';
import {getListingConfiguration, DEFAULT_DELIVERY_CONFIGS} from '/src/com/yext/listingslaunchstorm/js/enterprise/specifylistingconfiguration/datasource/getlistingsconfiguration';
import {ExpandedSyncSettings} from '/src/com/yext/listingslaunchstorm/js/enterprise/specifylistingconfiguration/expandedsyncsettings';
import {MatchingSettings} from '/src/com/yext/listingslaunchstorm/js/enterprise/specifylistingconfiguration/matchingsettings';
import {SyncSettings} from '/src/com/yext/listingslaunchstorm/js/enterprise/specifylistingconfiguration/syncsettings';
import {EnterpriseConfiguration} from '/src/com/yext/listingslaunchstorm/js/enterprise/taskflow/data/enterpriseconfiguration';
import {CalloutBanner, CalloutBannerType} from '/ui/components/calloutbanner/calloutbanner';
import {ContentSkeleton} from '/ui/components/contentskeleton/contentskeleton';

import * as styles from '/src/com/yext/listingslaunchstorm/js/enterprise/specifylistingconfiguration/specifylistingconfiguration.module.scss';

/**
 * @param {{
 *  enterpriseConfiguration: EnterpriseConfiguration,
 *  selectedListingConfig: (ListingConfiguration | undefined),
 *  onChangeListingConfig: function(ListingConfiguration): void,
 *  hasChooseEntitiesAndListingsConfigSetup: boolean,
 *  publisherIds: Array<number>,
 * }} props
* @returns {React.ReactNode}
*/
export function SpecifyListingConfiguration({enterpriseConfiguration, selectedListingConfig, onChangeListingConfig, hasChooseEntitiesAndListingsConfigSetup, publisherIds}) {
  /**
   * @param {AltBrandNamesConfig} altBrandConfig
   */
  function setAltBrand(altBrandConfig) {
    const deliveryConfigurationMap = selectedListingConfig?.deliveryConfigurationMap ?? DEFAULT_DELIVERY_CONFIGS;
    const applyToExistingSubAccounts = selectedListingConfig?.applyToExistingSubAccounts ?? false;
    const applyToNewSubAccounts = selectedListingConfig?.applyToNewSubAccounts ?? false;
    const profileField = altBrandConfig;
    const newConfig = new ListingConfiguration({
      profileField,
      deliveryConfigurationMap,
      applyToExistingSubAccounts,
      applyToNewSubAccounts,
    });
    onChangeListingConfig(newConfig);
  }

  /**
   * @param {!Map<number, DeliveryConfiguration>} deliveryConfigurationMap
   */
  function setDeliveryConfigs(deliveryConfigurationMap) {
    const profileField = selectedListingConfig?.profileField;
    const applyToExistingSubAccounts = selectedListingConfig?.applyToExistingSubAccounts ?? false;
    const applyToNewSubAccounts = selectedListingConfig?.applyToNewSubAccounts ?? false;
    const newConfig = new ListingConfiguration({
      profileField,
      deliveryConfigurationMap,
      applyToExistingSubAccounts,
      applyToNewSubAccounts,
    });
    onChangeListingConfig(newConfig);
  }

  const {status, data, refetch} = useAjax(true, getListingConfiguration);

  React.useEffect(() => {
    const loadExistingConfig = (profileField, deliveryConfigurationMap) => {
      const applyToExistingSubAccounts = selectedListingConfig?.applyToExistingSubAccounts ?? false;
      const applyToNewSubAccounts = selectedListingConfig?.applyToNewSubAccounts ?? false;
      const newConfig = new ListingConfiguration({
        profileField,
        deliveryConfigurationMap,
        applyToExistingSubAccounts,
        applyToNewSubAccounts,
      });
      onChangeListingConfig(newConfig);
    };

    if (selectedListingConfig?.profileField === undefined
        && selectedListingConfig?.deliveryConfigurationMap === undefined) {
      if (data !== undefined) {
        loadExistingConfig(
          data.existingConfig.profileField,
          data.existingConfig.deliveryConfigurationMap,
        );
      }
    }
  }, [data, selectedListingConfig, onChangeListingConfig]);

  if (status === 'error') {
    return (<ErrorCallout onReload={refetch} />);
  } else if (status !== 'success') {
    return (<ContentSkeleton type="box" height={100} />);
  } else {
    return (
      <div className={styles.page}>
        <OverrideCallout />

        <MatchingSettings
          selectedAltNameId={selectedListingConfig?.profileField?.id ?? ''}
          setSelectedAltBrand={setAltBrand}
          altBrandNames={data.alternateBrandNames}
        />

        {hasChooseEntitiesAndListingsConfigSetup
          ? <ExpandedSyncSettings
            enterpriseConfiguration={enterpriseConfiguration}
            syncSettings={selectedListingConfig?.deliveryConfigurationMap ?? DEFAULT_DELIVERY_CONFIGS}
            setSyncSettings={setDeliveryConfigs}
            publisherIds={publisherIds}
          />
          : <SyncSettings
            syncSettings={selectedListingConfig?.deliveryConfigurationMap ?? DEFAULT_DELIVERY_CONFIGS}
            setSyncSettings={setDeliveryConfigs}
            publisherIds={publisherIds}
          />}
      </div>
    );
  }
}

function OverrideCallout() {
  const callout = yext.msg('Any settings configured here will overwrite the default <strong>account-level</strong> settings. ');
  return (
    <CalloutBanner type={CalloutBannerType.WARNING}>
      <span dangerouslySetInnerHTML={{__html: callout}} />
    </CalloutBanner>
  );
}
